import {put, call, all} from 'redux-saga/effects';
import CalendarShare from "../../entities/calendarShare";
import * as Actions from "../actions/calendarShareActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CalendarShareAction = {
    calendarShareId: string;
    calendarShare: CalendarShare;
    calendarShares: CalendarShare[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCalendarShare(action: CalendarShareAction) {
	const session = tabSession.captureGeneration();
	try {
		const calendarShare: CalendarShare = yield call(api.findOne, 'calendarShare', action.calendarShareId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CALENDARSHARE_FETCH_ONE_SUCCESS, calendarShareId: action.calendarShareId, calendarShare, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CALENDARSHARE_FETCH_ONE_FAILURE, calendarShareId: action.calendarShareId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CalendarShareAction} action
 */
export function* saveCalendarShare(action: CalendarShareAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CALENDARSHARE_SAVE saga', action.calendarShare?.id);

		if (action.calendarShare === undefined || action.calendarShare === null) {
			console.error('saveCalendarShare received a null');
			throw new Error('saveCalendarShare received a null');
		}

		if (!(action.calendarShare instanceof CalendarShare)) {
			action.calendarShare = new CalendarShare(action.calendarShare);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.calendarShare.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.calendarShare);
			const addResult = yield call(idb.add, 'calendarShare', action.calendarShare);
			if (addResult.isErr()) {
				console.error(`⮂ saveCalendarShare: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCalendarShareSave(action.calendarShare, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CALENDARSHARE_SAVE_FAILURE, calendarShare: action.calendarShare, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CALENDARSHARE_SAVE, calendarShare: action.calendarShare, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCalendarShareSave(action.calendarShare, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CALENDARSHARE_SAVE_FAILURE, calendarShare: action.calendarShare, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CalendarShareAction} action
 * @param {CalendarShare[]} action.calendarShares
 */
export function* saveManyCalendarShares(action: CalendarShareAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CALENDARSHARE_SAVE_MANY saga', action.calendarShares.map(calendarShare => calendarShare.id).join(','));

        if (!Array.isArray(action.calendarShares)) {
            console.error('saveManyCalendarShares expected an array, received', action.calendarShares);
            throw new Error('saveManyCalendarShares expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCalendarShare), under these queue keys
		const dispatchedAddedOns: number[] = action.calendarShares.map(calendarShare => calendarShare?.addedOn);
		const revertAll = () => action.calendarShares.map((calendarShare, i) =>
			put(Actions.revertCalendarShareSave(calendarShare, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: CalendarShare[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.calendarShares.length; i++) {
				const calendarShare = action.calendarShares[i];
				calendarShare.persisted = false;
				ensureOperationId(calendarShare);
				toSave.push(calendarShare);
			}

			const addResult = yield call(idb.add, 'calendarShare', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCalendarShares: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CALENDARSHARE_SAVE_MANY_FAILURE, calendarShares: action.calendarShares, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CALENDARSHARE_SAVE_MANY, calendarShares: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CALENDARSHARE_SAVE_MANY_FAILURE, calendarShares: action.calendarShares, status: 500, err});

			console.error(`Error saving ${action.calendarShares.length} calendarShares to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CalendarShareAction} action
 */
export function* removeCalendarShare(action: CalendarShareAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.calendarShare === undefined || action.calendarShare === null || !(action.calendarShare instanceof CalendarShare)) {
			console.error('removeCalendarShare expected type CalendarShare, received', action.calendarShare);
			reportQueued(false);
			throw new Error('removeCalendarShare expected type CalendarShare');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.calendarShare.status = 'D';
		action.calendarShare.addedOn = newHRTimestamp();
		action.calendarShare.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.calendarShare);
		const addResult = yield call(idb.add, 'calendarShare', action.calendarShare);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCalendarShare: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CALENDARSHARE_REMOVE_FAILURE, calendarShare: action.calendarShare, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CALENDARSHARE_REMOVE, calendarShare: action.calendarShare});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CALENDARSHARE_REMOVE_FAILURE, calendarShare: action.calendarShare, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this CalendarShare.
	*
	* @param {CalendarShareAction} action
	*/
export function* restoreCalendarShare(action: CalendarShareAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCalendarShare expected getCurrentUserId() to return a value');
        return;
    }

	if (action.calendarShare === undefined || action.calendarShare === null) {
        console.error('restoreCalendarShare expected a CalendarShare, received null');
        throw new Error('restoreCalendarShare expected a CalendarShare, received null');
    }
    else if (!(action.calendarShare instanceof CalendarShare)) action.calendarShare = new CalendarShare(action.calendarShare); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.calendarShare.status = 'U';
		action.calendarShare.addedOn = newHRTimestamp();
		action.calendarShare.addedBy = userId;
		action.calendarShare.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.calendarShare);
		const addResult = yield call(idb.add, 'calendarShare', action.calendarShare);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCalendarShare: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CALENDARSHARE_RESTORE_FAILURE, calendarShare: action.calendarShare, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CALENDARSHARE_RESTORE, calendarShare: action.calendarShare});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CALENDARSHARE_RESTORE_FAILURE, calendarShare: action.calendarShare, status: 500, err});

		console.error('Error restoring from database', err);
	}
}